import type {
  TemplateCheckboxSettings,
  TemplateDateSettings,
  TemplateDropdownSettings,
  TemplateFieldSettings,
  TemplateFieldType,
  TemplateFieldTabIndexMode,
  TemplateImageSettings,
  TemplateRadioSettings,
  TemplateTextboxSettings,
} from '../../models/template.models';
import {
  FIELD_LABELS,
  FONT_ALIGNMENT_OPTIONS,
  FONT_FAMILY_OPTIONS,
  DEFAULT_FONT_SETTINGS,
  DEFAULT_SENDER_ACTIONS,
  TEXTBOX_RESIZE_OPTIONS,
  TEXTBOX_VALIDATION_OPTIONS,
} from './field-configurator.constants';
import type {
  FieldConfiguratorActor,
  FieldConfiguratorField,
  FieldConfiguratorPreviewFilter,
} from './field-configurator.models';

export const trimOrUndefined = (value: string | undefined): string | undefined => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

export const sanitizeOptionValues = (values: string[], fallback: string): string[] => {
  const seen = new Set<string>();
  const sanitized: string[] = [];

  for (const value of values) {
    const trimmed = value.trim();
    if (!trimmed || seen.has(trimmed)) {
      continue;
    }

    seen.add(trimmed);
    sanitized.push(trimmed);
  }

  return sanitized.length > 0 ? sanitized : [fallback];
};

export const clampIntegerOrUndefined = (value: number | undefined): number | undefined => {
  if (value === undefined || Number.isNaN(value)) {
    return undefined;
  }

  return Math.trunc(value);
};

export const createDefaultTextboxSettings = (): TemplateTextboxSettings => ({
  readOnly: false,
  maskValue: false,
  defaultText: undefined,
  hintText: undefined,
  resizeType: 'grow_vertically',
  fixedWidth: false,
  characterLimit: undefined,
  validationType: 'none',
  validationValue: undefined,
});

export const createDefaultDropdownSettings = (): TemplateDropdownSettings => ({
  hintText: undefined,
  options: ['Option1'],
  defaultOption: undefined,
});

export const createDefaultCheckboxSettings = (): TemplateCheckboxSettings => ({
  optionGroupId: undefined,
  value: 'Checkbox1',
  checked: false,
  size: 16,
  values: ['Checkbox1'],
  defaultValues: [],
});

export const createDefaultRadioSettings = (): TemplateRadioSettings => ({
  groupName: undefined,
  value: 'Radio1',
  checked: false,
  size: 16,
  values: ['Radio1'],
  defaultValue: undefined,
});

export const createDefaultDateSettings = (): TemplateDateSettings => ({
  dateFormat: 'MM/dd/yyyy',
  timeFormat: 'None',
  defaultDate: undefined,
});

export const createDefaultImageSettings = (): TemplateImageSettings => ({
  title: undefined,
  instruction: undefined,
  acceptJpgJpeg: true,
  acceptSvg: true,
  acceptPng: true,
  acceptBmp: true,
});

export const createDefaultFieldSettings = (): TemplateFieldSettings => ({
  common: {
    senderActions: { ...DEFAULT_SENDER_ACTIONS },
    dataSyncTag: undefined,
    tabIndexMode: 'auto',
    tabIndex: undefined,
    autoPositioningEnabled: false,
  },
  font: { ...DEFAULT_FONT_SETTINGS },
  textbox: createDefaultTextboxSettings(),
  dropdown: createDefaultDropdownSettings(),
  checkbox: createDefaultCheckboxSettings(),
  radio: createDefaultRadioSettings(),
  date: createDefaultDateSettings(),
  image: createDefaultImageSettings(),
});

export const normalizeFieldSettings = (settings: unknown): TemplateFieldSettings => {
  const defaults = createDefaultFieldSettings();
  const input = (settings && typeof settings === 'object' ? settings : {}) as Partial<TemplateFieldSettings>;
  const commonRaw = input.common;
  const tabIndexMode: TemplateFieldTabIndexMode = commonRaw?.tabIndexMode === 'custom' ? 'custom' : 'auto';
  const tabIndex = tabIndexMode === 'custom' ? clampIntegerOrUndefined(commonRaw?.tabIndex) : undefined;

  const dropdownOptions = sanitizeOptionValues(input.dropdown?.options ?? defaults.dropdown.options, 'Option1');
  const checkboxValues = sanitizeOptionValues(input.checkbox?.values ?? [], 'Checkbox1');
  const radioValues = sanitizeOptionValues(input.radio?.values ?? [], 'Radio1');
  const checkboxValue = trimOrUndefined(input.checkbox?.value) ?? checkboxValues[0] ?? defaults.checkbox.value;
  const radioValue = trimOrUndefined(input.radio?.value) ?? radioValues[0] ?? defaults.radio.value;

  return {
    common: {
      senderActions: {
        allowEditing: commonRaw?.senderActions?.allowEditing ?? defaults.common.senderActions.allowEditing,
        allowDeletion: commonRaw?.senderActions?.allowDeletion ?? defaults.common.senderActions.allowDeletion,
        requireDefaultValue:
          commonRaw?.senderActions?.requireDefaultValue ?? defaults.common.senderActions.requireDefaultValue,
      },
      dataSyncTag: trimOrUndefined(commonRaw?.dataSyncTag),
      tabIndexMode,
      tabIndex,
      autoPositioningEnabled: commonRaw?.autoPositioningEnabled ?? defaults.common.autoPositioningEnabled,
    },
    font: {
      family:
        input.font?.family && FONT_FAMILY_OPTIONS.includes(input.font.family)
          ? input.font.family
          : defaults.font.family,
      size: clampIntegerOrUndefined(input.font?.size) ?? defaults.font.size,
      lineHeight: clampIntegerOrUndefined(input.font?.lineHeight) ?? defaults.font.lineHeight,
      bold: input.font?.bold ?? defaults.font.bold,
      italic: input.font?.italic ?? defaults.font.italic,
      underline: input.font?.underline ?? defaults.font.underline,
      color: trimOrUndefined(input.font?.color) ?? defaults.font.color,
      align:
        input.font?.align && FONT_ALIGNMENT_OPTIONS.includes(input.font.align)
          ? input.font.align
          : defaults.font.align,
      rtl: input.font?.rtl ?? defaults.font.rtl,
      characterSpacing: clampIntegerOrUndefined(input.font?.characterSpacing) ?? defaults.font.characterSpacing,
    },
    textbox: {
      ...defaults.textbox,
      ...input.textbox,
      resizeType:
        input.textbox?.resizeType && TEXTBOX_RESIZE_OPTIONS.includes(input.textbox.resizeType)
          ? input.textbox.resizeType
          : defaults.textbox.resizeType,
      validationType:
        input.textbox?.validationType && TEXTBOX_VALIDATION_OPTIONS.includes(input.textbox.validationType)
          ? input.textbox.validationType
          : defaults.textbox.validationType,
      defaultText: trimOrUndefined(input.textbox?.defaultText),
      hintText: trimOrUndefined(input.textbox?.hintText),
      characterLimit: clampIntegerOrUndefined(input.textbox?.characterLimit),
      validationValue: trimOrUndefined(input.textbox?.validationValue),
    },
    dropdown: {
      ...defaults.dropdown,
      ...input.dropdown,
      hintText: trimOrUndefined(input.dropdown?.hintText),
      options: dropdownOptions,
      defaultOption:
        trimOrUndefined(input.dropdown?.defaultOption) &&
        dropdownOptions.includes(trimOrUndefined(input.dropdown?.defaultOption)!)
          ? trimOrUndefined(input.dropdown?.defaultOption)
          : undefined,
    },
    checkbox: {
      ...defaults.checkbox,
      ...input.checkbox,
      optionGroupId: trimOrUndefined(input.checkbox?.optionGroupId),
      value: checkboxValue,
      values: checkboxValues,
      defaultValues: sanitizeOptionValues(input.checkbox?.defaultValues ?? [], ''),
      checked:
        input.checkbox?.checked ??
        sanitizeOptionValues(input.checkbox?.defaultValues ?? [], '').includes(checkboxValue),
      size: clampIntegerOrUndefined(input.checkbox?.size) ?? defaults.checkbox.size,
    },
    radio: {
      ...defaults.radio,
      ...input.radio,
      groupName: trimOrUndefined(input.radio?.groupName),
      value: radioValue,
      values: radioValues,
      defaultValue: trimOrUndefined(input.radio?.defaultValue),
      checked: input.radio?.checked ?? trimOrUndefined(input.radio?.defaultValue) === radioValue,
      size: clampIntegerOrUndefined(input.radio?.size) ?? defaults.radio.size,
    },
    date: {
      ...defaults.date,
      ...input.date,
      defaultDate: trimOrUndefined(input.date?.defaultDate),
    },
    image: {
      ...defaults.image,
      ...input.image,
      title: trimOrUndefined(input.image?.title),
      instruction: trimOrUndefined(input.image?.instruction),
    },
  };
};

export const fieldSupportsCharacterSpacing = (fieldType: TemplateFieldType): boolean => fieldType !== 'name';

export const normalizeFieldSettingsForType = (
  fieldType: TemplateFieldType,
  settings: unknown,
): TemplateFieldSettings => {
  const normalized = normalizeFieldSettings(settings);
  if (fieldSupportsCharacterSpacing(fieldType)) {
    return normalized;
  }

  return {
    ...normalized,
    font: {
      ...normalized.font,
      characterSpacing: DEFAULT_FONT_SETTINGS.characterSpacing,
    },
  };
};

export const countFieldsForActor = (fields: FieldConfiguratorField[], actorId: string): number =>
  fields.filter((field) => field.role === actorId).length;

export const filterFieldsForSummary = (
  fields: FieldConfiguratorField[],
  previewFilter: FieldConfiguratorPreviewFilter,
): FieldConfiguratorField[] => {
  if (previewFilter === 'all') {
    return fields;
  }

  return fields.filter((field) => field.role === previewFilter);
};

const isRectInvalid = (rect: FieldConfiguratorField['rect']): boolean =>
  [rect.x, rect.y, rect.width, rect.height].some((value) => !Number.isFinite(value)) ||
  rect.width <= 0 ||
  rect.height <= 0 ||
  rect.x < 0 ||
  rect.y < 0 ||
  rect.x + rect.width > 1 ||
  rect.y + rect.height > 1;

const choiceGroupKey = (field: FieldConfiguratorField): string => {
  if (field.type === 'checkbox') {
    return `checkbox:${trimOrUndefined(normalizeFieldSettings(field.settings).checkbox.optionGroupId) ?? field.id}`;
  }
  if (field.type === 'radio') {
    return `radio:${trimOrUndefined(normalizeFieldSettings(field.settings).radio.groupName) ?? field.id}`;
  }
  return field.id;
};

const validateFieldSettings = (field: FieldConfiguratorField): string | null => {
  const settings = normalizeFieldSettings(field.settings);

  if (settings.common.tabIndexMode === 'custom' && (!settings.common.tabIndex || settings.common.tabIndex < 1)) {
    return `Field "${FIELD_LABELS[field.type]}" has an invalid custom tab index.`;
  }

  if (field.type === 'dropdown') {
    if (settings.dropdown.options.length === 0) {
      return 'Dropdown options must include at least one value.';
    }
    if (settings.dropdown.defaultOption && !settings.dropdown.options.includes(settings.dropdown.defaultOption)) {
      return 'Dropdown default option must match one of the options.';
    }
  }

  if (field.type === 'checkbox') {
    const values = sanitizeOptionValues(settings.checkbox.values ?? [settings.checkbox.value ?? ''], 'Checkbox1');
    if (values.length === 0) {
      return 'Checkbox values must include at least one value.';
    }
  }

  if (field.type === 'radio') {
    const values = sanitizeOptionValues(settings.radio.values ?? [settings.radio.value ?? ''], 'Radio1');
    if (values.length === 0) {
      return 'Radio values must include at least one value.';
    }
  }

  return null;
};

const validateChoiceGroup = (anchor: FieldConfiguratorField, fields: FieldConfiguratorField[]): string | null => {
  if (anchor.type !== 'checkbox' && anchor.type !== 'radio') {
    return null;
  }

  const groupKey = choiceGroupKey(anchor);
  const groupFields = fields.filter((field) => choiceGroupKey(field) === groupKey);
  if (groupFields.length <= 1) {
    return null;
  }

  const first = groupFields[0]!;
  if (!groupFields.every((field) => field.role === first.role)) {
    return `${FIELD_LABELS[anchor.type]} options in the same group must have the same recipient role.`;
  }
  if (!groupFields.every((field) => field.page === first.page)) {
    return `${FIELD_LABELS[anchor.type]} options in the same group must stay on the same page.`;
  }
  if (!groupFields.every((field) => field.required === first.required)) {
    return `${FIELD_LABELS[anchor.type]} options in the same group must share the same required setting.`;
  }
  if (anchor.type === 'radio' && !trimOrUndefined(normalizeFieldSettings(anchor.settings).radio.groupName)) {
    return 'Radio group name is required for grouped radio options.';
  }

  return null;
};

export const validateFieldConfiguratorStep = (
  fields: FieldConfiguratorField[],
  actors: FieldConfiguratorActor[],
  pageCount: number,
  messages: {
    emptyFields: string;
    missingActor: (actor: FieldConfiguratorActor) => string;
    unknownActor: string;
    outOfBounds: (field: FieldConfiguratorField) => string;
  },
): string | null => {
  if (fields.length === 0) {
    return messages.emptyFields;
  }

  const missingActor = actors.find((actor) => countFieldsForActor(fields, actor.id) === 0);
  if (missingActor) {
    return messages.missingActor(missingActor);
  }

  const validatedChoiceGroups = new Set<string>();
  for (const field of fields) {
    if (!actors.some((actor) => actor.id === field.role)) {
      return messages.unknownActor;
    }

    if (field.page < 1 || field.page > Math.max(pageCount, 1)) {
      return messages.outOfBounds(field);
    }

    if (isRectInvalid(field.rect)) {
      return `Field "${FIELD_LABELS[field.type]}" has invalid bounds.`;
    }

    if (field.type === 'checkbox' || field.type === 'radio') {
      const groupKey = choiceGroupKey(field);
      if (!validatedChoiceGroups.has(groupKey)) {
        validatedChoiceGroups.add(groupKey);
        const groupError = validateChoiceGroup(field, fields);
        if (groupError) {
          return groupError;
        }
      }
    }

    const settingsError = validateFieldSettings(field);
    if (settingsError) {
      return settingsError;
    }
  }

  return null;
};
